/**
 * Generic resource list page.
 *
 * The browser owns rendering and interaction state only. Table structure,
 * operation visibility, confirmation copy, command identity, route targets,
 * and API bindings are fetched from the authenticated backend resolver.
 */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ActionDescriptor, TranslationKey } from '@finance/contracts';
import { DataListPage } from './DataListPage';
import type { TableActionContext } from './TableRenderer';
import { PageForbiddenState, PageLoadingState, PageNotFoundState } from '../states';
import { ResourceLoadErrorState } from './ResourceLoadErrorState';
import { AUTHORIZATION_CONTEXT_CHANGED_EVENT } from '../../features/auth/session-provider';
import { useExperience } from '../../features/experience/ExperienceProvider';
import { useResolvedResource } from '../../features/experience/useResolvedExperience';
import {
  executeResolvedResourceAction,
  experienceQueryKeys,
} from '../../features/experience/experience-api';
import { createGenericResourceDataSource } from '../../features/experience/generic-resource-data-source';
import { resolveTKey, translateRuntimeTKey } from '../../i18n/resolve-key';
import { useUiStore } from '../../state/ui';

interface ListHookResult {
  data?: unknown;
  isLoading: boolean;
  isError: boolean;
  error?: Error | null | unknown;
  refetch: () => void;
}

const EMPTY_LIST_RESULT: ListHookResult = {
  data: [],
  isLoading: false,
  isError: false,
  error: null,
  refetch: () => undefined,
};

function useEmptyList(): ListHookResult {
  return EMPTY_LIST_RESULT;
}

export interface GenericResourceListPageProps {
  resourceId: string;
  useList?: () => ListHookResult;
  rowIdExtractor?: (row: Record<string, unknown>) => string | undefined;
  /**
   * Handles only browser-owned localUi actions while preserving generic command
   * execution, confirmation, cache invalidation, and success/error messaging.
   */
  onLocalAction?: (
    action: ActionDescriptor,
    row: Record<string, unknown>,
    context?: TableActionContext
  ) => void | Promise<void>;
  onExport?: (options: {
    scope: 'page' | 'selection' | 'all';
    format: 'csv' | 'xlsx';
    fields?: string[];
  }) => void;
  /**
   * Route-owned equality filters applied to every server-table request.
   * These restrictions may narrow a resource but cannot grant visibility.
   */
  requiredFilters?: Readonly<Record<string, unknown>>;
  /** Overrides the resource list title for a route-specific resource view. */
  titleKey?: TranslationKey;
}

function defaultRowIdExtractor(
  rowIdField: string
): (row: Record<string, unknown>) => string | undefined {
  return row => {
    const value = row[rowIdField] ?? row.id;
    return value == null ? undefined : String(value);
  };
}

function fillFirstRouteParameter(path: string, value: string): string {
  return path.replace(/:[A-Za-z0-9_-]+/, encodeURIComponent(value));
}

export function GenericResourceListPage({
  resourceId,
  useList,
  rowIdExtractor,
  onLocalAction,
  onExport,
  requiredFilters,
  titleKey,
}: GenericResourceListPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useUiStore();
  const experience = useExperience();
  const descriptorQuery = useResolvedResource(resourceId);
  const resolved = descriptorQuery.data;
  const resource = resolved?.resource;
  const tableDescriptor = resolved?.table;
  // Detail activation is derived solely from the resolved read descriptor.
  // Deprecated browser flags can restrict route composition but cannot replace
  // an absent read operation with an interactive row.
  const rowActivationAction = tableDescriptor?.rowActivationAction;
  const dataSource = useMemo(
    () =>
      resolved?.bindings.list
        ? createGenericResourceDataSource(resolved.bindings.list, requiredFilters)
        : undefined,
    [requiredFilters, resolved]
  );
  const effectiveUseList = dataSource ? useEmptyList : (useList ?? useEmptyList);
  const [submitting, setSubmitting] = useState(false);

  const translate = useCallback(
    (key: unknown, fallback: TranslationKey) => {
      if (typeof key === 'string') {
        const translated = translateRuntimeTKey(t, key);
        if (translated) return translated;
      }
      return t(resolveTKey(fallback));
    },
    [t],
  );

  const executeCommand = useCallback(
    async (
      action: ActionDescriptor,
      row: Record<string, unknown>,
      confirmationValues?: Record<string, unknown>,
    ) => {
      if (!tableDescriptor || submitting) return;
      const rowId = row[tableDescriptor.rowIdField] ?? row.id;
      if (rowId === undefined || rowId === null) {
        addToast({ type: 'error', message: translate(action.errorMessageKey, 'common:error.unexpected') });
        return;
      }
      setSubmitting(true);
      try {
        await executeResolvedResourceAction(resourceId, action.id, {
          id: String(rowId),
          ...(typeof row.version === 'number' && Number.isSafeInteger(row.version) && row.version >= 0
            ? { expectedVersion: row.version }
            : {}),
          ...(typeof row.expectedUpdatedAt === 'string' && row.expectedUpdatedAt.length > 0
            ? { expectedUpdatedAt: row.expectedUpdatedAt }
            : {}),
          ...(confirmationValues ?? {}),
        });
        await queryClient.invalidateQueries({ queryKey: experienceQueryKeys.resource(resourceId) });
        if (resourceId === 'organization-module-activations') {
          window.dispatchEvent(new Event(AUTHORIZATION_CONTEXT_CHANGED_EVENT));
        }
        if (action.successMessageKey) {
          addToast({ type: 'success', message: translate(action.successMessageKey, 'common:success') });
        }
      } catch (error) {
        addToast({ type: 'error', message: translate(action.errorMessageKey, 'common:error.unexpected') });
        // ActionRenderer only applies success navigation/telemetry after this
        // promise resolves. Re-throw to make a failed mutation unambiguously
        // non-successful to every caller.
        throw error;
      } finally {
        setSubmitting(false);
      }
    },
    [addToast, queryClient, resourceId, submitting, tableDescriptor, translate]
  );

  const handleAction = useCallback(
    async (
      action: ActionDescriptor,
      row: Record<string, unknown>,
      context?: TableActionContext
    ) => {
      if (action.kind === 'localUi' && onLocalAction) {
        await onLocalAction(action, row, context);
        return;
      }
      if (action.kind !== 'command' || !action.commandId || !action.operationId) return;
      if (action.confirmation && context?.confirmationHandled !== true) return;
      await executeCommand(action, row, context?.confirmationValues);
    },
    [executeCommand, onLocalAction]
  );

  const createAction = tableDescriptor?.createAction;

  const handleRowActivate = useCallback(
    (row: Record<string, unknown>) => {
      if (
        resource?.rowActivation !== 'DETAIL' ||
        !resource.detailRouteId ||
        !resource.readOperationId
      ) return;
      const id = row[tableDescriptor?.rowIdField ?? 'id'] ?? row.id;
      const detailPath = experience.getRoutePath(resource.detailRouteId);
      if (id == null || !detailPath) return;
      navigate(fillFirstRouteParameter(detailPath, String(id)));
    },
    [experience, navigate, resource, tableDescriptor?.rowIdField]
  );

  if (descriptorQuery.isLoading || experience.loading) return <PageLoadingState />;
  if (descriptorQuery.isError || experience.error) {
    return (
      <ResourceLoadErrorState
        error={descriptorQuery.error ?? experience.error}
        onRetry={() => void Promise.all([descriptorQuery.refetch(), experience.refresh()])}
      />
    );
  }
  if (!resolved || !resource) return <PageNotFoundState />;
  if (resolved.availability !== 'AVAILABLE' && resolved.availability !== 'READ_ONLY') {
    return (
      <PageForbiddenState
        reasonCode={resolved.reasonCode ?? 'RESOURCE_READ_DENIED'}
        decisionId={resolved.decisionId}
      />
    );
  }
  if (!tableDescriptor || (!useList && !dataSource)) return <PageNotFoundState />;

  const extractId = dataSource
    ? defaultRowIdExtractor(tableDescriptor.rowIdField)
    : (rowIdExtractor ?? defaultRowIdExtractor(tableDescriptor.rowIdField));
  const effectiveTableDescriptor = resource.descriptionKey && !tableDescriptor.descriptionKey
    ? { ...tableDescriptor, descriptionKey: resource.descriptionKey }
    : tableDescriptor;

  return (
    <DataListPage
      titleKey={titleKey ?? resource.listTitleKey}
      useList={effectiveUseList}
      dataSource={dataSource}
      tableInstanceKey={resourceId}
      rowIdExtractor={extractId}
      onRowActivate={handleRowActivate}
      onAction={handleAction}
      onExport={onExport}
      descriptor={effectiveTableDescriptor}
      rowActivationAction={rowActivationAction}
      createAction={createAction}
    />
  );
}
